<template>
  <div>
    <div
      v-if="showDownload"
      class="absolute bottom-0 left-0 right-0 flex flex-row justify-end w-full p-4 pb-4 space-x-3 bg-white z-modalLayer"
      role="toolbar"
      aria-label="Resume download and close actions"
    >
      <template v-if="resume.state === ResumeState.SOURCE">
        <AppButton
          color="dark"
          type="line"
          :loading="downloadingResume"
          @click="downloadResume"
          aria-label="Download resume"
        >
          <font-awesome-icon
            class="mr-2"
            :icon="['far', 'arrow-down-to-line']"
            aria-hidden="true"
          />
          <span>Download</span>
        </AppButton>
      </template>
      <template v-else-if="resume.state === ResumeState.OPTIMIZED">
        <AppButton
          data-test="download-markdown-docx-button"
          color="dark"
          type="line"
          :loading="downloadingResume"
          @click="downloadMarkdownResumeAsDocx"
          aria-label="Download resume as Microsoft Word document"
        >
          <font-awesome-icon
            class="mr-2"
            :icon="['far', 'arrow-down-to-line']"
            aria-hidden="true"
          />
          <span>Microsoft Word (.docx)</span>
        </AppButton>
      </template>
      <template v-else>
        <AppButton
          color="dark"
          type="line"
          :loading="downloadingResume"
          @click="downloadResumeAsDocx"
          aria-label="Download resume as Microsoft Word document"
        >
          <font-awesome-icon
            class="mr-2"
            :icon="['far', 'arrow-down-to-line']"
            aria-hidden="true"
          />
          <span>Microsoft Word (.docx)</span>
        </AppButton>
        <AppButton
          color="dark"
          type="line"
          :loading="downloadingResume"
          @click="downloadResume"
          aria-label="Download resume as PDF document"
        >
          <font-awesome-icon
            class="mr-2"
            :icon="['far', 'arrow-down-to-line']"
            aria-hidden="true"
          />
          <span>PDF Document (.pdf)</span>
        </AppButton>
      </template>
      <AppButton
        v-if="!downloadingResume"
        @click="close"
        :aria-label="
          resume.fromLinkedIn ? 'Continue to next step' : 'Close preview modal'
        "
      >
        <span>{{ resume.fromLinkedIn ? 'Continue' : 'Close' }}</span>
      </AppButton>
    </div>
    <img
      v-if="shouldDisplayPreviewImage"
      :src="resume.previewImage"
      alt="Resume Preview"
      @error="snapshotPreview = false"
    />
    <div v-else id="resumeViewStatic" class="block shadow-lg">
      <div
        class="w-full p-16 bg-white min-h-[1100px]"
        v-if="!resume?.data?.basics"
      >
        <SkeletonLoader width="60" height="3" :count="4" class="mb-12" />
        <SkeletonLoader
          height="3"
          :count="6"
          :randomize="true"
          :minWidth="65"
          :maxWidth="100"
          class="mb-6"
        />
        <SkeletonLoader
          height="3"
          :count="8"
          :randomize="true"
          :minWidth="65"
          :maxWidth="100"
          class="mb-6"
        />
        <SkeletonLoader
          height="3"
          :count="6"
          :randomize="true"
          :minWidth="65"
          :maxWidth="100"
          class="mb-6"
        />
      </div>
      <div v-else class="staticPreviewScaler">
        <MarkdownResumeView
          v-if="resume.state === ResumeState.OPTIMIZED && resume?.data?.text"
          :content="resume?.data?.text"
        />
        <StyleWrapper
          v-else
          class="min-h-[1100px] print:min-h-[0px]"
          :resume="resume?.data"
        >
          <component
            class="resumeTemplateStyler"
            :is="resumeTemplate"
            :resume="resume?.data"
          ></component>
        </StyleWrapper>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, toRefs, ref } from 'vue'
import AppButton from '@/components/ui/AppButton/AppButton.vue'
import StyleWrapper from '@/components/resume/components/support/StyleWrapper.vue'
import BasicResume from '@/components/resume/templates/basic/Index.vue'
import BasicAltResume from '@/components/resume/templates/basicalt/Index.vue'
import ModernResume from '@/components/resume/templates/modern/Index.vue'
import ModernAltResume from '@/components/resume/templates/modernalt/Index.vue'
import ColumnsResume from '@/components/resume/templates/columns/Index.vue'
import ColumnsAltResume from '@/components/resume/templates/columnsalt/Index.vue'
import MordernStudentResume from '@/components/resume/templates/mordernStudent/Index.vue'
import ModernProfessionalResume from '@/components/resume/templates/modernProfessional/Index.vue'
import BaylorUndergraduateResume from '@/components/resume/templates/baylorUndergraduate/Index.vue'
import BaylorBswResume from '@/components/resume/templates/baylorBsw/Index.vue'
import classicResume from '@/components/resume/templates/classic/Index.vue'
import KansasBusinessFinResume from '@/components/resume/templates/kansasBusinessFin/Index.vue'
import KansasBusinessUgResume from '@/components/resume/templates/kansasBusinessUg/Index.vue'
import SkeletonLoader from '@/components/ui/Loaders/SkeletonLoader.vue'
import { useScaling } from '@/composables/useScaling'
import resume from '@/lang/en/findings/resume'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import { useHtmlExport } from '@/composables/useHtmlExport'
import { html2pdf } from '@/api/v4/pdf'
import { downloadResume as apiDownloadResume } from '@/api/v4/resume'
import { useFileDownload } from '@/composables/useFileDownload'
import { ResumeState } from '@/types'
import { useRoute } from 'vue-router'
import { dispatchUserEvent, UserEvents } from '@/lib/userEvents'
import { generateDocx } from '@/lib/docx'
import MarkdownResumeView from '@/components/resume/v2/MarkdownResumeView.vue'
import { downloadMarkdownComprehensively } from '@/lib/markdownToDocx'

export default defineComponent({
  name: 'ResumeViewStatic',
  components: { StyleWrapper, SkeletonLoader, AppButton, MarkdownResumeView },
  props: {
    resume: {
      type: Object as PropType<any>,
      required: true,
    },
    showDownload: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const downloadingResume = ref(false)
    const { resume } = toRefs(props)
    const store = useStructuredResumeStore()
    const route = useRoute()
    store.setPowerEdit(false)

    const { downloadBlob, createDownloadLink } = useFileDownload()
    const { getStyledHtml } = useHtmlExport()
    const snapshotPreview = ref(true)

    const shouldDisplayPreviewImage = computed(() => {
      return (
        snapshotPreview.value &&
        resume.value.state === ResumeState.SOURCE &&
        resume.value.previewImage
      )
    })

    function close() {
      if (resume.value.fromLinkedIn) {
        document.getElementById('jobDescriptionInput')?.focus()
      }
      emit('close')
    }
    const downloadHtmlAsPdf = async () => {
      const element = document.querySelector(
        '#resumeViewStatic .resumeStyler'
      ) as HTMLElement
      const { data, error } = await html2pdf(
        getStyledHtml(element),
        'resume',
        resume.value.id
      )
      if (error) {
        return console.error(error)
      }
      downloadBlob(data, `${resume.value?.name}.pdf` || 'resume.pdf')
      dispatchUserEvent({
        name: UserEvents.ResumeDownloaded,
        context: {
          target: route.name || 'unknown',
          message: 'Download PDF',
        },
      })
    }

    const downloadOriginalResume = async () => {
      const { data, error } = await apiDownloadResume(resume.value.id)

      if (error) {
        return downloadHtmlAsPdf()
      }

      createDownloadLink(data.url, data.fileName)
      dispatchUserEvent({
        name: UserEvents.ResumeDownloaded,
        context: {
          target: route.name || 'unknown',
          message: 'Download Original Resume',
        },
      })
    }

    const downloadResume = async () => {
      downloadingResume.value = true

      if (resume.value.state === ResumeState.SOURCE) {
        await downloadOriginalResume()
      } else {
        await downloadHtmlAsPdf()
      }

      downloadingResume.value = false
    }

    const downloadResumeAsDocx = async () => {
      if (resume.value.state === ResumeState.SOURCE) return
      const documentName = `${resume.value?.name || 'resume'}`
      return generateDocx(resume.value.data, documentName, resume.value)
    }

    const downloadMarkdownResumeAsDocx = async () => {
      if (
        resume.value.state !== ResumeState.OPTIMIZED ||
        !resume.value.data?.text
      ) {
        return
      }

      // Get the current resume markdown with all applied suggestions
      const currentMarkdown = resume.value.data.text

      try {
        const documentName = `${resume.value?.name || 'resume'}.docx`

        // Download as DOCX using the comprehensive converter
        await downloadMarkdownComprehensively(currentMarkdown, {
          filename: documentName,
          font: 'Calibri',
          fontSize: 22,
          headerFontSize: 24,
          nameFontSize: 32,
        })
      } catch (error) {
        console.error('Error downloading resume:', error)
      }
    }

    useScaling('staticPreviewScaler')

    const templates = {
      basic: BasicResume,
      basicalt: BasicAltResume,
      modern: ModernResume,
      modernalt: ModernAltResume,
      columns: ColumnsResume,
      columnsalt: ColumnsAltResume,
      modernstudent: MordernStudentResume,
      modernprofessional: ModernProfessionalResume,
      baylorundergraduate: BaylorUndergraduateResume,
      baylorbsw: BaylorBswResume,
      classic: classicResume,
      kubusug: KansasBusinessUgResume,
      kubusfin: KansasBusinessFinResume,
    }

    const resumeTemplate = computed(
      () => templates[resume.value?.data?.meta?.template] || BasicResume
    )
    return {
      resume,
      resumeTemplate,
      downloadResume,
      downloadingResume,
      shouldDisplayPreviewImage,
      snapshotPreview,
      ResumeState,
      downloadResumeAsDocx,
      close,
      downloadMarkdownResumeAsDocx,
    }
  },
})
</script>

<style lang="postcss" scoped>
:deep(.sidebar) {
  section {
    @apply break-inside-avoid-page;
  }
}
:deep(.main) {
  section {
    h3,
    .title {
      @apply break-after-avoid-page break-inside-avoid;
    }
  }
}
</style>
